import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ZoomIn, Pause, Play, RotateCcw } from "lucide-react";
import { getDirectionalText } from "@/lib/textDirection";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TIMER_DURATION = 65;

/* ═══════════════════════════════════════════════════════════════
   AudioPlayer — exact copy from QuestionPage.jsx
   ═══════════════════════════════════════════════════════════════ */
function AudioPlayer({ url }) {
  const audioRef = useRef(null);
  const [playing, setPlaying]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else         { a.play().catch(() => {}); setPlaying(true); }
  };

  const restart = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => {});
    setPlaying(true);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
    setProgress(ratio * 100);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div style={{ flex:1, minHeight:0, width:"100%", display:"flex", justifyContent:"center", alignItems:"center" }}>
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={e => {
          const a = e.target;
          if (a.duration) setProgress((a.currentTime / a.duration) * 100);
        }}
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onEnded={() => setPlaying(false)}
      />
      <div style={{
        width:"100%", maxWidth:"480px",
        background:"rgba(10,4,6,0.80)", backdropFilter:"blur(16px)",
        border:"1.5px solid rgba(245,200,66,0.28)",
        borderRadius:"20px", padding:"28px 28px 24px",
        boxShadow:"0 0 0 3px rgba(245,200,66,0.06), 0 16px 50px rgba(0,0,0,0.65)",
      }}>
        <div style={{ textAlign:"center", marginBottom:"18px" }}>
          <span style={{ fontSize:"2.4rem", lineHeight:1 }}>🎵</span>
        </div>
        <div
          onClick={seek}
          style={{ cursor:"pointer", height:"6px", borderRadius:"99px", background:"rgba(255,255,255,0.10)", position:"relative", marginBottom:"10px" }}
        >
          <div style={{ height:"100%", borderRadius:"99px", width:`${progress}%`, background:"linear-gradient(90deg,#D4BC30,#F5E99A)", transition:"width 0.25s linear" }} />
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:"0.7rem", color:"rgba(245,200,66,0.45)", fontWeight:700, marginBottom:"20px", fontVariantNumeric:"tabular-nums" }}>
          <span>{fmt(audioRef.current?.currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>
        <div style={{ display:"flex", gap:"12px", justifyContent:"center", alignItems:"center" }}>
          <button
            onClick={restart}
            style={{ background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(245,200,66,0.18)", borderRadius:"50%", width:"42px", height:"42px", cursor:"pointer", color:"rgba(245,200,66,0.55)", display:"flex", alignItems:"center", justifyContent:"center" }}
          >
            <RotateCcw size={16} />
          </button>
          <button
            onClick={toggle}
            style={{ background:"linear-gradient(135deg,#D4BC30,#F5E99A)", border:"none", borderRadius:"50%", width:"58px", height:"58px", cursor:"pointer", color:"#130104", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 24px rgba(245,200,66,0.35)" }}
          >
            {playing ? <Pause size={22} /> : <Play size={22} style={{ marginLeft:"2px" }} />}
          </button>
          <div style={{ width:"42px" }} />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PlayerPreviewModal
   ═══════════════════════════════════════════════════════════════ */
export default function PlayerPreviewModal({
  question,            // single question object
  questions,           // optional array (for sequential category preview)
  initialIndex = 0,    // starting index in questions[]
  catName = "",        // category display name
  onClose,             // close modal
  token,               // admin JWT token
}) {
  /* ── Sequential mode ── */
  const isSequential = Array.isArray(questions) && questions.length > 0;
  const [qIndex, setQIndex] = useState(initialIndex);
  const currentQ = isSequential ? questions[qIndex] : question;

  /* ── Timer ── */
  const [timeLeft, setTimeLeft]       = useState(TIMER_DURATION);
  const [timerOn, setTimerOn]         = useState(true);
  const [tensionDone, setTensionDone] = useState(false);
  const timerRef = useRef(null);

  /* ── Answer reveal ── */
  const [showAnswer, setShowAnswer]   = useState(false);

  /* ── Image zoom ── */
  const [zoomedImage, setZoomedImage] = useState(null);

  /* ── Checklist ── */
  const [checklist, setChecklist]     = useState({});
  const [checklistSaved, setChecklistSaved] = useState(false);
  const [saving, setSaving]           = useState(false);

  /* ── Reset when question changes ── */
  useEffect(() => {
    setTimeLeft(TIMER_DURATION);
    setTimerOn(true);
    setTensionDone(false);
    setShowAnswer(false);
    setZoomedImage(null);
    setChecklist({});
    setChecklistSaved(false);
  }, [qIndex, question?.id]);

  /* ── Lock scroll on mount ── */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  /* ── Timer tick ── */
  useEffect(() => {
    if (!timerOn || timeLeft <= 0) return;
    timerRef.current = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [timeLeft, timerOn]);

  /* ── Tension + buzz sounds ── */
  useEffect(() => {
    if (timeLeft === 10 && !tensionDone && timerOn) { setTensionDone(true); playTension(); }
    if (timeLeft === 0 && timerOn) {
      setTimerOn(false);
      playBuzz();
      toast.error("⏰ انتهى الوقت!");
    }
  }, [timeLeft, timerOn, tensionDone]);

  const playTension = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const schedule = (freq, t, dur) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "triangle"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.25, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + dur);
        osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + dur + 0.01);
      };
      for (let i = 0; i < 10; i++) schedule(i % 2 === 0 ? 830 : 600, i * 0.85, 0.35);
    } catch {}
  };

  const playBuzz = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sawtooth"; osc.frequency.value = 150;
      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.8);
    } catch {}
  };

  /* ── Derived ── */
  if (!currentQ) return null;

  const pct      = (timeLeft / TIMER_DURATION) * 100;
  const R        = 30;
  const circ     = 2 * Math.PI * R;
  const dash     = circ * (1 - pct / 100);
  const timerCol = timeLeft > 20 ? "#43e97b" : timeLeft > 10 ? "#f5c842" : "#ff6b6b";

  const diffBadge = currentQ.difficulty === 300
    ? { label:"سهل",   color:"#43e97b", bg:"rgba(67,233,123,0.12)", border:"rgba(67,233,123,0.30)" }
    : currentQ.difficulty === 600
    ? { label:"متوسط", color:"#f5c842", bg:"rgba(245,200,66,0.12)",  border:"rgba(245,200,66,0.30)" }
    : { label:"صعب",   color:"#ff6b6b", bg:"rgba(255,107,107,0.12)", border:"rgba(255,107,107,0.30)" };

  const hasAnswerImg = !!currentQ.answer_image_url;
  const hasAnswerTxt = !!currentQ.answer;
  const { text: questionText, dir: questionDir } = getDirectionalText(currentQ.text);
  const { text: answerText, dir: answerDir } = getDirectionalText(currentQ.answer);

  const glass = {
    background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)",
    backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
    borderRadius:"16px", boxShadow:"0 8px 32px rgba(0,0,0,0.35)",
  };

  /* ── Checklist items (dynamic per question type) ── */
  const checklistItems = [
    { id:"text_clear",    label:"نص السؤال واضح ومفهوم" },
    { id:"answer_ok",     label:"الإجابة الصحيحة محددة ودقيقة" },
    { id:"no_typos",      label:"لا توجد أخطاء إملائية أو لغوية" },
    { id:"difficulty_ok", label:"مستوى الصعوبة مناسب للنقاط" },
    { id:"category_ok",   label:"الفئة المحددة صحيحة" },
    ...(currentQ.image_url || currentQ.answer_image_url
      ? [{ id:"image_ok", label:"الصورة واضحة وتطابق السؤال" }] : []),
    ...(currentQ.audio_url
      ? [{ id:"audio_ok", label:"الصوت يعمل بشكل صحيح" }] : []),
    ...(currentQ.choices?.length > 0
      ? [{ id:"choices_ok", label:"الخيارات منطقية ومعقولة" }] : []),
  ];

  const allChecked = checklistItems.every(item => checklist[item.id]);

  const handleSaveChecklist = async () => {
    if (!allChecked || saving || checklistSaved) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/admin/questions/${currentQ.id}/preview`,
        { checklist, all_checked: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChecklistSaved(true);
      toast.success("✅ تم حفظ المراجعة");
    } catch {
      toast.error("تعذّر حفظ المراجعة");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (!isSequential) return;
    if (qIndex < questions.length - 1) setQIndex(qIndex + 1);
    else { toast("✅ انتهت كل أسئلة الفئة"); onClose(); }
  };

  const handlePrev = () => {
    if (!isSequential || qIndex === 0) return;
    setQIndex(qIndex - 1);
  };

  /* ═══════ JSX ═══════ */
  return (
    <div
      style={{
        position:"fixed", inset:0, zIndex:9000,
        background:"linear-gradient(rgba(10,0,0,0.62),rgba(10,0,0,0.80)), url('https://images.pexels.com/photos/159862/art-school-of-athens-raphael-italian-painter-fresco-159862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920') center/cover fixed no-repeat",
        fontFamily:"'Tajawal','Cairo',sans-serif",
        direction:"rtl",
        display:"flex", flexDirection:"column",
        overflow:"hidden",
      }}
    >
      {/* ── Fonts + CSS animations (same as QuestionPage) ── */}
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap" />
      <style>{`
        @keyframes pv-fadeInScale {
          from { opacity:0; transform:scale(0.88); }
          to   { opacity:1; transform:scale(1); }
        }
        @keyframes pv-fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes pv-answerPop {
          0%  { opacity:0; transform:scale(0.80); }
          70% { transform:scale(1.04); opacity:1; }
          100%{ transform:scale(1);   opacity:1; }
        }
        @keyframes pv-glowPulse {
          0%,100%{ box-shadow:0 0 20px rgba(67,233,123,0.3); }
          50%    { box-shadow:0 0 40px rgba(67,233,123,0.6); }
        }
        .pv-answer-wrap  { animation: pv-fadeIn 0.25s ease forwards; }
        .pv-answer-box   { animation: pv-fadeInScale 0.32s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .pv-answer-pop   { animation: pv-answerPop 0.50s cubic-bezier(0.34,1.56,0.64,1) 0.12s both; }
        .pv-reveal-btn:hover { transform:translateY(-2px) !important; }
        .pv-checklist-item { transition: background 0.18s, border-color 0.18s; }
        .pv-checklist-item:hover { background:rgba(245,200,66,0.10) !important; }
      `}</style>

      {/* ════════ ZOOM MODAL ════════ */}
      {zoomedImage && (
        <div
          style={{ position:"fixed", inset:0, zIndex:9200, background:"rgba(0,0,0,0.96)", backdropFilter:"blur(10px)", display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setZoomedImage(null)}
        >
          <div style={{ position:"relative", maxWidth:"90vw" }} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setZoomedImage(null)}
              style={{ position:"absolute", top:"-40px", left:0, color:"rgba(255,255,255,0.6)", background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:"6px", fontSize:"0.9rem" }}
            >
              <X size={18}/> إغلاق
            </button>
            <img src={zoomedImage} alt="zoomed" style={{ maxWidth:"100%", maxHeight:"88vh", objectFit:"contain", borderRadius:"18px", display:"block", boxShadow:"0 0 100px rgba(0,0,0,0.95)" }} />
          </div>
        </div>
      )}

      {/* ════════ ANSWER MODAL (modified — no team assignment) ════════ */}
      {showAnswer && (
        <div
          className="pv-answer-wrap"
          style={{ position:"fixed", inset:0, zIndex:9100, background:"rgba(0,0,0,0.80)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px", overflowY:"auto" }}
        >
          <div
            className="pv-answer-box"
            style={{
              background:"linear-gradient(160deg,rgba(30,27,75,0.97),rgba(15,12,41,0.99))",
              border:"1.5px solid rgba(255,255,255,0.14)",
              borderRadius:"24px",
              padding:"clamp(24px,4vh,44px) clamp(20px,4vw,48px)",
              width:"min(620px,94vw)",
              textAlign:"center",
              boxShadow:"0 30px 80px rgba(0,0,0,0.65)",
              backdropFilter:"blur(24px)",
              position:"relative",
            }}
          >
            {/* Admin badge */}
            <div style={{ position:"absolute", top:"14px", right:"16px", background:"rgba(245,200,66,0.12)", border:"1px solid rgba(245,200,66,0.28)", borderRadius:"20px", padding:"3px 12px", fontSize:"0.62rem", fontWeight:800, color:"rgba(245,200,66,0.70)", letterSpacing:"0.06em" }}>
              وضع المعاينة
            </div>

            {/* Answer label */}
            <div style={{ color:"rgba(255,255,255,0.40)", fontSize:"0.8rem", fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:"12px" }}>
              ✦  الإجابة  ✦
            </div>

            {/* Answer image */}
            {hasAnswerImg && (
              <div className="pv-answer-pop" style={{ display:"flex", justifyContent:"center", marginBottom:"20px" }}>
                <div
                  onClick={() => setZoomedImage(currentQ.answer_image_url)}
                  onMouseEnter={e => e.currentTarget.style.transform="scale(1.03)"}
                  onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}
                  style={{
                    position:"relative", display:"inline-flex", alignItems:"center", justifyContent:"center",
                    cursor:"zoom-in", padding:"6px", borderRadius:"20px",
                    background:"linear-gradient(135deg,rgba(245,200,66,0.14),rgba(255,140,66,0.08),rgba(245,200,66,0.14))",
                    border:"1.5px solid rgba(245,200,66,0.42)",
                    boxShadow:"0 0 0 1px rgba(245,200,66,0.08), 0 0 36px rgba(245,200,66,0.18), 0 14px 44px rgba(0,0,0,0.70)",
                    transition:"transform 0.25s",
                  }}
                >
                  <img
                    src={currentQ.answer_image_url}
                    alt="answer"
                    onError={e => { e.target.parentElement.style.display="none"; }}
                    style={{ display:"block", maxHeight:"clamp(110px,20vh,220px)", maxWidth:"min(100%,340px)", objectFit:"contain", borderRadius:"14px" }}
                  />
                  <div style={{ position:"absolute", bottom:"10px", left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,0.62)", backdropFilter:"blur(6px)", borderRadius:"7px", padding:"3px 10px", color:"rgba(245,200,66,0.82)", fontSize:"0.58rem", fontWeight:800, pointerEvents:"none", whiteSpace:"nowrap" }}>
                    <ZoomIn size={9} style={{ display:"inline", marginLeft:"3px", verticalAlign:"middle" }} />
                    اضغط للتكبير
                  </div>
                </div>
              </div>
            )}

            {/* Answer text */}
            {hasAnswerTxt && (
              <div
                className="pv-answer-pop"
                dir={answerDir}
                style={{ fontSize:"clamp(1.8rem,4.5vw,3.2rem)", fontWeight:800, color:"#ffffff", lineHeight:1.15, marginBottom:"20px", textShadow:"0 0 50px rgba(245,200,66,0.45), 0 3px 14px rgba(0,0,0,0.8)", direction:answerDir, unicodeBidi:"isolate" }}
              >
                {answerText}
              </div>
            )}

            {/* Choices (for MCQ) */}
            {currentQ.choices?.length > 0 && (
              <div style={{ marginBottom:"20px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px" }}>
                {currentQ.choices.map((ch, i) => {
                  const isCorrect = ch === currentQ.answer;
                  return (
                    <div
                      key={i}
                      style={{
                        padding:"10px 14px", borderRadius:"12px", textAlign:"right",
                        background: isCorrect ? "rgba(67,233,123,0.18)" : "rgba(255,255,255,0.05)",
                        border: `1.5px solid ${isCorrect ? "rgba(67,233,123,0.55)" : "rgba(255,255,255,0.10)"}`,
                        color: isCorrect ? "#43e97b" : "rgba(255,255,255,0.60)",
                        fontSize:"0.88rem", fontWeight: isCorrect ? 800 : 600,
                        display:"flex", alignItems:"center", gap:"8px",
                      }}
                    >
                      <span style={{ fontSize:"0.75rem", flexShrink:0 }}>{isCorrect ? "✓" : String.fromCharCode(65+i)}</span>
                      {ch}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Points badge */}
            <div style={{ display:"flex", justifyContent:"center", marginBottom:"20px" }}>
              <div style={{ background:"linear-gradient(135deg,#f5c842,#ff8c42)", color:"#1a1208", fontWeight:900, fontSize:"clamp(0.95rem,2vw,1.35rem)", borderRadius:"50px", padding:"8px 28px", boxShadow:"0 6px 20px rgba(245,200,66,0.40)", display:"inline-flex", alignItems:"center", gap:"6px" }}>
                <span>+{currentQ.difficulty}</span>
                <span style={{ fontWeight:700, fontSize:"0.75em", opacity:0.75 }}>نقطة</span>
              </div>
            </div>

            {/* ── PREVIEW CHECKLIST ── */}
            <div style={{
              background:"rgba(0,0,0,0.30)", border:"1px solid rgba(255,255,255,0.08)",
              borderRadius:"18px", padding:"18px 20px", marginBottom:"20px", textAlign:"right",
            }}>
              <div style={{ color:"rgba(255,255,255,0.50)", fontSize:"0.68rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:"14px", textAlign:"center" }}>
                ✅  قائمة مراجعة القبول
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                {checklistItems.map(item => {
                  const checked = !!checklist[item.id];
                  return (
                    <label
                      key={item.id}
                      className="pv-checklist-item"
                      style={{
                        display:"flex", alignItems:"center", gap:"10px",
                        padding:"9px 12px", borderRadius:"10px", cursor:"pointer",
                        background: checked ? "rgba(67,233,123,0.12)" : "rgba(255,255,255,0.04)",
                        border:`1px solid ${checked ? "rgba(67,233,123,0.35)" : "rgba(255,255,255,0.08)"}`,
                      }}
                    >
                      <div
                        onClick={() => setChecklist(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                        style={{
                          width:"20px", height:"20px", borderRadius:"6px", flexShrink:0,
                          border:`2px solid ${checked ? "#43e97b" : "rgba(255,255,255,0.25)"}`,
                          background: checked ? "#43e97b" : "transparent",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          transition:"all 0.18s",
                        }}
                      >
                        {checked && <span style={{ color:"#0a1208", fontSize:"0.75rem", fontWeight:900, lineHeight:1 }}>✓</span>}
                      </div>
                      <span
                        onClick={() => setChecklist(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                        style={{ color: checked ? "rgba(255,255,255,0.90)" : "rgba(255,255,255,0.50)", fontSize:"0.82rem", fontWeight: checked ? 700 : 500, flex:1 }}
                      >
                        {item.label}
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* Save checklist */}
              {!checklistSaved ? (
                <button
                  onClick={handleSaveChecklist}
                  disabled={!allChecked || saving}
                  style={{
                    marginTop:"14px", width:"100%", padding:"11px", borderRadius:"50px", border:"none",
                    background: allChecked ? "linear-gradient(135deg,#43e97b,#38f9d7)" : "rgba(255,255,255,0.08)",
                    color: allChecked ? "#0a1208" : "rgba(255,255,255,0.25)",
                    fontWeight:800, fontSize:"0.92rem", cursor: allChecked ? "pointer" : "not-allowed",
                    fontFamily:"'Tajawal','Cairo',sans-serif",
                    boxShadow: allChecked ? "0 4px 18px rgba(67,233,123,0.30)" : "none",
                    transition:"all 0.22s", opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? "⏳ جارٍ الحفظ..." : allChecked ? "✅ تم التحقق — حفظ المراجعة" : `${checklistItems.filter(i=>checklist[i.id]).length}/${checklistItems.length} — أكمل القائمة أولاً`}
                </button>
              ) : (
                <div style={{ marginTop:"14px", textAlign:"center", color:"#43e97b", fontWeight:800, fontSize:"0.88rem" }}>
                  ✅ تمت المراجعة وحُفظت بنجاح
                </div>
              )}
            </div>

            {/* ── NAVIGATION BUTTONS ── */}
            <div style={{ display:"flex", gap:"10px", justifyContent:"center", flexWrap:"wrap" }}>
              <button
                onClick={onClose}
                style={{
                  background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.18)",
                  color:"rgba(255,255,255,0.70)", borderRadius:"50px",
                  padding:"11px 28px", fontSize:"0.88rem", fontWeight:700, cursor:"pointer",
                  fontFamily:"'Tajawal','Cairo',sans-serif", transition:"all 0.18s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.14)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.07)"; }}
              >
                ← الرجوع للإدارة
              </button>

              {isSequential && (
                <>
                  {qIndex > 0 && (
                    <button
                      onClick={() => { setShowAnswer(false); handlePrev(); }}
                      style={{
                        background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.18)",
                        color:"rgba(255,255,255,0.70)", borderRadius:"50px",
                        padding:"11px 22px", fontSize:"0.88rem", fontWeight:700, cursor:"pointer",
                        fontFamily:"'Tajawal','Cairo',sans-serif", transition:"all 0.18s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.14)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.07)"; }}
                    >
                      → السابق
                    </button>
                  )}
                  <button
                    onClick={() => { setShowAnswer(false); handleNext(); }}
                    style={{
                      background:"linear-gradient(135deg,#f5c842,#ff8c42)",
                      border:"none", color:"#1a1208", borderRadius:"50px",
                      padding:"11px 28px", fontSize:"0.88rem", fontWeight:800, cursor:"pointer",
                      fontFamily:"'Tajawal','Cairo',sans-serif",
                      boxShadow:"0 4px 18px rgba(245,200,66,0.40)", transition:"all 0.18s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform="translateY(-2px)"}
                    onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}
                  >
                    {qIndex < questions.length - 1 ? "السؤال التالي ←" : "إنهاء المعاينة ✓"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════ TOP BAR ════════ */}
      <header
        style={{
          flexShrink:0, position:"relative", zIndex:9010,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"0 clamp(12px,2vw,24px)",
          height:"clamp(48px,6.5vh,62px)",
          background:"rgba(255,255,255,0.07)",
          borderBottom:"1px solid rgba(255,255,255,0.09)",
          backdropFilter:"blur(14px)",
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{ display:"flex", alignItems:"center", gap:"6px", color:"rgba(255,255,255,0.40)", fontWeight:700, fontSize:"clamp(0.72rem,1.2vw,0.88rem)", background:"none", border:"none", cursor:"pointer", transition:"color 0.18s", fontFamily:"'Tajawal','Cairo',sans-serif" }}
          onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,0.80)"}
          onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,0.40)"}
        >
          <X size={16}/><span>إغلاق</span>
        </button>

        {/* Center: category + difficulty */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"2px" }}>
          <span style={{ color:"rgba(245,200,66,0.85)", fontWeight:800, fontSize:"clamp(0.78rem,1.4vw,1rem)", display:"flex", alignItems:"center", gap:"6px" }}>
            <span style={{ fontSize:"0.65rem", fontWeight:600, background:"rgba(245,200,66,0.12)", border:"1px solid rgba(245,200,66,0.25)", borderRadius:"20px", padding:"2px 10px", color:"rgba(245,200,66,0.70)" }}>وضع المعاينة 👁</span>
          </span>
          <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
            {catName && <span style={{ color:"rgba(255,255,255,0.38)", fontSize:"clamp(0.6rem,0.95vw,0.74rem)", fontWeight:600 }}>{catName}</span>}
            {catName && <span style={{ color:"rgba(255,255,255,0.15)" }}>·</span>}
            <span style={{ color:diffBadge.color, fontSize:"clamp(0.6rem,0.95vw,0.74rem)", fontWeight:800 }}>{diffBadge.label}</span>
            {isSequential && (
              <>
                <span style={{ color:"rgba(255,255,255,0.15)" }}>·</span>
                <span style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.68rem", fontWeight:700 }}>{qIndex + 1} / {questions.length}</span>
              </>
            )}
          </div>
        </div>

        {/* Sequential nav (right) */}
        {isSequential ? (
          <div style={{ display:"flex", gap:"6px" }}>
            <button
              onClick={handlePrev}
              disabled={qIndex === 0}
              style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)", color:"rgba(255,255,255,0.55)", borderRadius:"8px", padding:"5px 12px", cursor: qIndex === 0 ? "not-allowed" : "pointer", fontSize:"0.72rem", fontWeight:700, opacity: qIndex === 0 ? 0.3 : 1, fontFamily:"'Tajawal','Cairo',sans-serif" }}
            >→ السابق</button>
            <button
              onClick={() => { setShowAnswer(false); handleNext(); }}
              style={{ background:"rgba(245,200,66,0.12)", border:"1px solid rgba(245,200,66,0.30)", color:"#f5c842", borderRadius:"8px", padding:"5px 12px", cursor:"pointer", fontSize:"0.72rem", fontWeight:700, fontFamily:"'Tajawal','Cairo',sans-serif" }}
            >{qIndex < questions.length - 1 ? "التالي ←" : "إنهاء ✓"}</button>
          </div>
        ) : (
          <div style={{ width:"clamp(100px,12vw,160px)" }} />
        )}
      </header>

      {/* ════════ MAIN BODY — centered single column ════════ */}
      <div
        style={{
          flex:1, display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center",
          padding:"clamp(12px,1.5vw,20px)",
          overflow:"hidden", minHeight:0,
          position:"relative", zIndex:9010,
        }}
      >
        {/* Center question panel */}
        <div style={{
          ...glass,
          borderRadius:"24px",
          border:"2px solid rgba(245,200,66,0.35)",
          boxShadow:"0 0 60px rgba(245,200,66,0.08), 0 20px 60px rgba(0,0,0,0.55)",
          display:"flex", flexDirection:"column",
          width:"min(820px,100%)",
          maxHeight:"calc(100vh - clamp(48px,6.5vh,62px) - 40px)",
          overflow:"hidden", minHeight:0,
        }}>

          {/* ── Panel header: timer + difficulty badge ── */}
          <div style={{
            flexShrink:0,
            display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"clamp(10px,1.4vh,16px) clamp(14px,2vw,22px)",
            background:"rgba(245,200,66,0.05)",
            borderBottom:"1px solid rgba(245,200,66,0.15)",
          }}>
            {/* Difficulty info */}
            <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
              <div style={{ background:diffBadge.bg, border:`1px solid ${diffBadge.border}`, borderRadius:"10px", padding:"6px 14px", textAlign:"center" }}>
                <div style={{ color:diffBadge.color, fontSize:"clamp(1.1rem,2vw,1.6rem)", fontWeight:900, lineHeight:1 }}>{currentQ.difficulty}</div>
                <div style={{ color:diffBadge.color, fontSize:"0.58rem", fontWeight:700, opacity:0.75, marginTop:"2px" }}>{diffBadge.label}</div>
              </div>
              {currentQ.question_type && currentQ.question_type !== "text" && (
                <div style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"8px", padding:"4px 10px", fontSize:"0.65rem", fontWeight:800, color:"rgba(255,255,255,0.50)" }}>
                  {currentQ.question_type === "image" ? "🖼️ صورة" : currentQ.question_type === "audio" ? "🎵 صوت" : currentQ.question_type}
                </div>
              )}
            </div>

            {/* Timer */}
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"6px" }}>
              <div style={{ position:"relative", width:"clamp(72px,8.5vw,90px)", height:"clamp(72px,8.5vw,90px)" }}>
                <svg width="100%" height="100%" viewBox="0 0 70 70" style={{ overflow:"visible" }}>
                  <circle cx="35" cy="35" r={R} fill="rgba(0,0,0,0.3)" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
                  <circle
                    cx="35" cy="35" r={R}
                    fill="none" stroke={timerCol} strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={circ} strokeDashoffset={dash}
                    transform="rotate(-90 35 35)"
                    style={{ transition:"stroke-dashoffset 1s linear, stroke 0.5s ease" }}
                  />
                  <text x="35" y="35" textAnchor="middle" dominantBaseline="central" fill={timerCol} fontSize="17" fontWeight="900" fontFamily="Tajawal,Cairo,sans-serif">
                    {timeLeft}
                  </text>
                </svg>
              </div>
              <div style={{ display:"flex", gap:"5px" }}>
                <button
                  onClick={() => setTimerOn(t => !t)}
                  style={{ display:"flex", alignItems:"center", gap:"3px", background:timerOn?"rgba(245,200,66,0.13)":"rgba(67,233,123,0.13)", border:`1px solid ${timerOn?"rgba(245,200,66,0.35)":"rgba(67,233,123,0.35)"}`, color:timerOn?"#f5c842":"#43e97b", borderRadius:"8px", padding:"3px 7px", cursor:"pointer", fontSize:"0.58rem", fontWeight:800, fontFamily:"Tajawal,Cairo,sans-serif" }}
                >
                  {timerOn ? <Pause size={9}/> : <Play size={9}/>}
                  {timerOn?"إيقاف":"تشغيل"}
                </button>
                <button
                  onClick={() => { setTimeLeft(TIMER_DURATION); setTimerOn(false); setTensionDone(false); }}
                  style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.55)", borderRadius:"8px", padding:"4px 5px", cursor:"pointer" }}
                  title="إعادة"
                >
                  <RotateCcw size={9}/>
                </button>
                <button
                  onClick={() => { setTimeLeft(TIMER_DURATION); setTimerOn(true); setTensionDone(false); }}
                  style={{ display:"flex", alignItems:"center", gap:"3px", background:"rgba(67,233,123,0.10)", border:"1px solid rgba(67,233,123,0.28)", color:"rgba(67,233,123,0.80)", borderRadius:"8px", padding:"3px 7px", cursor:"pointer", fontSize:"0.58rem", fontWeight:800, fontFamily:"Tajawal,Cairo,sans-serif" }}
                >
                  <Play size={9}/>ابدأ
                </button>
              </div>
            </div>

            {/* Points badge */}
            <div style={{ background:"linear-gradient(135deg,#f5c842,#ff8c42)", color:"#1a1208", fontWeight:900, fontSize:"clamp(1.3rem,2.6vw,2.1rem)", borderRadius:"50px", padding:"8px 18px", textAlign:"center", boxShadow:"0 4px 16px rgba(245,200,66,0.40)", lineHeight:1 }}>
              {currentQ.difficulty}
              <div style={{ fontSize:"0.55em", fontWeight:700, opacity:0.7, marginTop:"2px" }}>نقطة</div>
            </div>
          </div>

          {/* ── Panel body: question text + image + audio ── */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"clamp(10px,1.5vh,18px) clamp(12px,2vw,24px)", overflowY:"auto", minHeight:0, gap:"clamp(8px,1.2vh,14px)" }}>

            {/* Question text */}
            <p
              data-testid="preview-question-text"
              dir={questionDir}
              style={{
                margin:"0 auto", textAlign:"center", color:"#ffffff", fontWeight:700, lineHeight:1.5,
                direction:questionDir, unicodeBidi:"isolate",
                textShadow:"0 2px 16px rgba(0,0,0,0.55)", maxWidth:"720px", flexShrink:0,
                fontSize: (currentQ.image_url || currentQ.audio_url)
                  ? "clamp(1.15rem,2.4vw,1.85rem)"
                  : "clamp(1.9rem,4.4vw,3.4rem)",
              }}
            >
              {questionText}
            </p>

            {/* Audio player */}
            {currentQ.audio_url && <AudioPlayer url={currentQ.audio_url} />}

            {/* Question image */}
            {currentQ.image_url && (
              <div style={{ width:"100%", display:"flex", justifyContent:"center", alignItems:"center", flexShrink:0 }}>
                <div style={{ position:"relative", display:"inline-flex", alignItems:"center" }}>
                  <img
                    src={currentQ.image_url}
                    alt="question"
                    onClick={() => setZoomedImage(currentQ.image_url)}
                    onError={e => { e.target.parentElement.parentElement.style.display="none"; }}
                    style={{
                      display:"block",
                      maxHeight:"clamp(160px,32vh,340px)",
                      maxWidth:"min(100%,560px)",
                      objectFit:"contain", borderRadius:"14px",
                      border:"1.5px solid rgba(245,200,66,0.28)",
                      boxShadow:"0 0 0 3px rgba(245,200,66,0.08), 0 10px 32px rgba(0,0,0,0.65)",
                      cursor:"zoom-in", transition:"transform 0.25s",
                    }}
                    onMouseEnter={e => e.target.style.transform="scale(1.02)"}
                    onMouseLeave={e => e.target.style.transform="scale(1)"}
                  />
                  <div style={{ position:"absolute", bottom:"6px", left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,0.60)", backdropFilter:"blur(4px)", borderRadius:"6px", padding:"2px 8px", color:"rgba(245,200,66,0.75)", fontSize:"0.58rem", fontWeight:800, pointerEvents:"none", whiteSpace:"nowrap" }}>
                    <ZoomIn size={9} style={{ display:"inline", marginLeft:"3px", verticalAlign:"middle" }} />
                    اضغط للتكبير
                  </div>
                </div>
              </div>
            )}

            {/* Choices preview (MCQ) */}
            {currentQ.choices?.length > 0 && !showAnswer && (
              <div style={{ width:"100%", maxWidth:"640px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", flexShrink:0 }}>
                {currentQ.choices.map((ch, i) => (
                  <div
                    key={i}
                    style={{
                      padding:"10px 14px", borderRadius:"12px", textAlign:"right",
                      background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.12)",
                      color:"rgba(255,255,255,0.75)", fontSize:"0.88rem", fontWeight:600,
                      display:"flex", alignItems:"center", gap:"8px",
                    }}
                  >
                    <span style={{ fontSize:"0.75rem", color:"rgba(245,200,66,0.55)", fontWeight:800, flexShrink:0 }}>{String.fromCharCode(65+i)}</span>
                    {ch}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Panel footer: reveal button ── */}
          <div style={{ flexShrink:0, padding:"clamp(10px,1.5vh,16px) clamp(16px,3vw,32px)", borderTop:"1px solid rgba(255,255,255,0.07)", display:"flex", justifyContent:"center" }}>
            {!showAnswer ? (
              <button
                className="pv-reveal-btn"
                onClick={() => { setTimerOn(false); setShowAnswer(true); }}
                style={{
                  background:"linear-gradient(135deg,#f5c842,#ff8c42)",
                  border:"none", borderRadius:"50px",
                  padding:"clamp(10px,1.5vh,15px) clamp(32px,5.5vw,56px)",
                  fontSize:"clamp(0.92rem,1.6vw,1.12rem)",
                  fontWeight:800, color:"#1a1208", cursor:"pointer",
                  boxShadow:"0 6px 22px rgba(245,200,66,0.38)",
                  transition:"transform 0.22s, box-shadow 0.22s",
                  fontFamily:"'Tajawal','Cairo',sans-serif",
                }}
              >
                كشف الإجابة
              </button>
            ) : (
              <div style={{ color:"rgba(255,255,255,0.28)", fontSize:"clamp(0.7rem,1.1vw,0.82rem)", fontWeight:700 }}>
                ↑ الإجابة ظاهرة أعلاه
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
